// Back office -> Tables & Reservations -> Reservations: the reservation desk.
// The day's book (restaurant time), and "New reservation" for a guest on the
// phone. Times and free tables come from GET /api/table-management/
// reservations/availability - the online flow's own rules, never computed here.
// The form and the day's book are lib/reservationDesk (shared with the dining Floor screen).
import React, { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { toast } from 'sonner';
import {
  AlertTriangle, CalendarDays, ChevronLeft, ChevronRight, Clock, Gift, Globe, Loader2, MessageSquare, Minus, Pencil,
  Phone, Plus, ShieldAlert, Users,
} from 'lucide-react';
import {
  COUNTRY_CODES, OCCASIONS, dayLabel, hhmm12, reservationActions, savedText, shiftDay, statusDoneText,
  useReservationDay, useReservationForm,
} from '@/lib/reservationDesk';

const STATUS_STYLE = {
  pending: 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30',
  confirmed: 'bg-teal-50 text-teal-700 ring-teal-200 dark:bg-teal-500/10 dark:text-teal-300 dark:ring-teal-500/30',
  seated: 'bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-500/30',
  completed: 'bg-slate-100 text-slate-600 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700',
  cancelled: 'bg-slate-100 text-slate-500 ring-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700',
  no_show: 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/30',
};

/** The back office's way to call /api/table-management (full URLs, its own fetch helper). */
function useTmClient(api, TM) {
  return useMemo(() => ({
    get: (path, query) => api(`${TM}${path}${query ? `?${new URLSearchParams(query)}` : ''}`),
    post: (path, body) => api(`${TM}${path}`, { method: 'POST', body: JSON.stringify(body) }),
    patch: (path, body) => api(`${TM}${path}`, { method: 'PATCH', body: JSON.stringify(body || {}) }),
  }), [api, TM]);
}

export default function ReservationDesk({ api, TM }) {
  const client = useTmClient(api, TM);
  const { day, setDay, today, zone, data, error, load, setStatus: patchStatus, active, closed, guests } = useReservationDay(client);
  const [editing, setEditing] = useState(null); // {mode: 'create'} | {mode: 'edit', reservation}
  const [busyId, setBusyId] = useState(null);

  const setStatus = async (r, status, ask) => {
    if (ask && !window.confirm(ask)) return;
    setBusyId(r.id);
    try {
      await patchStatus(r, status);
      toast.success(statusDoneText(r, status));
      load();
    } catch (e) { toast.error(e.message); }
    setBusyId(null);
  };

  return (
    <div className="space-y-4" data-testid="reservation-desk">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          <button className="h-9 w-9 flex items-center justify-center text-slate-500 hover:text-slate-900 dark:hover:text-white" aria-label="Previous day" onClick={() => day && setDay(shiftDay(day, -1))}><ChevronLeft className="w-4 h-4" /></button>
          <Input type="date" value={day || ''} onChange={(e) => e.target.value && setDay(e.target.value)} className="w-40 h-9 border-0 shadow-none focus-visible:ring-0" data-testid="reservations-date" />
          <button className="h-9 w-9 flex items-center justify-center text-slate-500 hover:text-slate-900 dark:hover:text-white" aria-label="Next day" onClick={() => day && setDay(shiftDay(day, 1))}><ChevronRight className="w-4 h-4" /></button>
        </div>
        {today && day !== today && <Button variant="ghost" size="sm" onClick={() => setDay(today)}>Today</Button>}
        <div className="text-sm text-slate-500">
          <span className="font-medium text-slate-800 dark:text-slate-200">{dayLabel(day, today)}</span>
          <span className="mx-1.5">·</span>{active.length} {active.length === 1 ? 'booking' : 'bookings'} · {guests} {guests === 1 ? 'guest' : 'guests'}
        </div>
        <Button className="ml-auto bg-teal-600 hover:bg-teal-700 text-white" onClick={() => setEditing({ mode: 'create' })} data-testid="new-reservation-btn">
          <Phone className="w-4 h-4 mr-1.5" /> New reservation
        </Button>
      </div>
      {zone && <p className="text-xs text-slate-400 flex items-center gap-1"><Globe className="w-3.5 h-3.5" /> Times are the restaurant's ({zone}).</p>}
      {error && <div className="rounded-lg border border-rose-200 bg-rose-50 dark:bg-rose-500/10 dark:border-rose-500/30 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">{error}</div>}

      {data && active.length === 0 && (
        <Card><CardContent className="py-12 text-center">
          <CalendarDays className="w-8 h-8 mx-auto text-slate-300 mb-2" />
          <p className="font-medium text-slate-700 dark:text-slate-200">No reservations for this day</p>
          <p className="text-sm text-slate-500 mt-1">Guest calling? Take the booking with New reservation.</p>
        </CardContent></Card>
      )}

      <div className="space-y-2">
        {active.map((r) => <ReservationRow key={r.id} r={r} busy={busyId === r.id} onStatus={setStatus} onEdit={() => setEditing({ mode: 'edit', reservation: r })} />)}
      </div>
      {closed.length > 0 && (
        <details className="group">
          <summary className="cursor-pointer text-sm text-slate-500 select-none">Cancelled & no-show ({closed.length})</summary>
          <div className="space-y-2 mt-2 opacity-75">
            {closed.map((r) => <ReservationRow key={r.id} r={r} busy={busyId === r.id} onStatus={setStatus} onEdit={null} />)}
          </div>
        </details>
      )}

      {editing && (
        <ReservationSheet client={client} reservation={editing.reservation}
          defaultDate={day || today} today={today} zone={zone}
          onClose={() => setEditing(null)}
          onSaved={(answer) => {
            const said = savedText(answer, editing.mode === 'edit');
            if (said.ok) toast.success(said.text); else toast.warning(said.text);
            setEditing(null);
            if (answer.reservation.date !== day) setDay(answer.reservation.date); else load();
          }} />
      )}
    </div>
  );
}

function ReservationRow({ r, busy, onStatus, onEdit }) {
  return (
    <Card className="overflow-hidden" data-testid={`reservation-${r.id}`}>
      <CardContent className="p-0 flex flex-wrap items-stretch">
        <div className="w-24 shrink-0 flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-800/60 border-r border-slate-100 dark:border-slate-800 py-3">
          <span className="text-lg font-semibold tabular-nums text-slate-900 dark:text-white">{hhmm12(r.time).replace(/ (AM|PM)/, '')}</span>
          <span className="text-[11px] uppercase tracking-wide text-slate-400">{hhmm12(r.time).slice(-2)}</span>
        </div>
        <div className="flex-1 min-w-[220px] px-4 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-semibold text-slate-900 dark:text-white">{r.customer_name}</p>
            <span className="inline-flex items-center gap-1 text-sm text-slate-500"><Users className="w-3.5 h-3.5" />{r.party_size}</span>
            <span className={`text-[11px] font-medium capitalize px-2 py-0.5 rounded-full ring-1 ${STATUS_STYLE[r.status] || STATUS_STYLE.completed}`}>{r.status.replace('_', ' ')}</span>
            {r.source === 'phone'
              ? <span className="inline-flex items-center gap-1 text-[11px] text-slate-500"><Phone className="w-3 h-3" /> Phone{r.created_by_name ? ` · ${r.created_by_name}` : ''}</span>
              : <span className="inline-flex items-center gap-1 text-[11px] text-slate-500"><Globe className="w-3 h-3" /> Online</span>}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
            <span className="tabular-nums">{r.customer_phone}</span>
            {!r.client_verified && <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400" title="The guest has not confirmed this number by code"><ShieldAlert className="w-3 h-3" /> Unverified</span>}
            {r.confirmation_texted_at && <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Confirmation texted</span>}
            {r.occasion && <span className="inline-flex items-center gap-1 text-violet-600 dark:text-violet-300"><Gift className="w-3 h-3" /> {r.occasion}</span>}
            {r.special_requests && <span className="truncate max-w-[280px]">“{r.special_requests}”</span>}
          </div>
          {r.allergies && (
            <p className="mt-1.5 inline-flex items-center gap-1 rounded-md bg-rose-50 dark:bg-rose-500/10 px-2 py-0.5 text-xs font-medium text-rose-700 dark:text-rose-300">
              <AlertTriangle className="w-3 h-3" /> Allergy: {r.allergies}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="text-center">
            <p className="text-[10px] uppercase tracking-wide text-slate-400">Table</p>
            <p className="font-semibold text-slate-800 dark:text-slate-100">{r.table_number || '—'}</p>
          </div>
          <div className="flex flex-wrap gap-1.5 justify-end">
            {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-400 self-center" />}
            {reservationActions(r, { canEdit: !!onEdit }).map((x) => (
              <Button key={x.label} size="sm" disabled={busy} variant={['primary', 'seat'].includes(x.tone) ? 'default' : 'outline'}
                className={x.tone === 'seat' ? 'bg-violet-600 hover:bg-violet-700 text-white' : x.tone === 'danger' ? 'text-rose-600 hover:text-rose-700' : undefined}
                onClick={() => (x.edit ? onEdit() : onStatus(r, x.status, x.ask))}>
                {x.edit && <Pencil className="w-3.5 h-3.5 mr-1" />}{x.label}
              </Button>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Section({ title, hint, children }) {
  return (
    <section className="space-y-2">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{title}</p>
        {hint && <p className="text-xs text-slate-500">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

function ReservationSheet({ client, reservation, defaultDate, today, zone, onClose, onSaved }) {
  const {
    editing, form, set, avail, loadingAvail, slots, freeTables, phone, phoneOk, slotFree, ready, saving, problem, save,
  } = useReservationForm({ client, reservation, defaultDate: defaultDate || today, onSaved });

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-xl p-0 flex flex-col" data-testid="reservation-sheet">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-slate-100 dark:border-slate-800 text-left">
          <SheetTitle className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-teal-50 dark:bg-teal-500/10 text-teal-600 flex items-center justify-center">{editing ? <Pencil className="w-4 h-4" /> : <Phone className="w-4 h-4" />}</span>
            {editing ? `Edit ${reservation.confirmation_code}` : 'New reservation'}
          </SheetTitle>
          <SheetDescription>{editing ? 'Changes are checked against the book like a new booking.' : 'Taking a booking over the phone. Confirmed as soon as you save.'}</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          <Section title="Guest">
            <Input autoFocus placeholder="Guest name" value={form.name} onChange={(e) => set({ name: e.target.value })} data-testid="resv-name" />
            <div className="flex gap-2">
              <select className="h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 text-sm" value={form.code} onChange={(e) => set({ code: e.target.value })} aria-label="Country code">
                {COUNTRY_CODES.map(([c, label]) => <option key={c} value={c}>{c} {label}</option>)}
              </select>
              <Input type="tel" inputMode="tel" placeholder="(555) 867-5309" value={form.phone} onChange={(e) => set({ phone: e.target.value })} data-testid="resv-phone" />
            </div>
            {form.phone && !phoneOk && <p className="text-xs text-rose-600">Enter the full phone number.</p>}
            {form.phone && phoneOk && <p className="text-xs text-slate-400">Saved as {phone}. Added to your client list as unverified.</p>}
            <Input type="email" placeholder="Email (optional)" value={form.email} onChange={(e) => set({ email: e.target.value })} />
          </Section>

          <Section title="Party">
            <div className="flex items-center gap-3">
              <div className="flex items-center rounded-lg border border-slate-200 dark:border-slate-700">
                <button type="button" className="h-10 w-10 flex items-center justify-center text-slate-500 disabled:opacity-40" disabled={form.party <= 1} onClick={() => set({ party: form.party - 1, tableId: null })} aria-label="One fewer guest"><Minus className="w-4 h-4" /></button>
                <span className="w-12 text-center text-lg font-semibold tabular-nums" data-testid="resv-party">{form.party}</span>
                <button type="button" className="h-10 w-10 flex items-center justify-center text-slate-500 disabled:opacity-40" disabled={form.party >= 99} onClick={() => set({ party: form.party + 1, tableId: null })} aria-label="One more guest"><Plus className="w-4 h-4" /></button>
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {[2, 4, 6, 8].map((n) => (
                  <button key={n} type="button" onClick={() => set({ party: n, tableId: null })}
                    className={`h-8 px-3 rounded-full text-sm border ${form.party === n ? 'border-teal-600 bg-teal-50 text-teal-700 dark:bg-teal-500/10 dark:text-teal-300' : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'}`}>{n}</button>
                ))}
              </div>
            </div>
          </Section>

          <Section title="When" hint={zone ? `Restaurant time (${zone})` : null}>
            <Input type="date" min={today || undefined} value={form.date} onChange={(e) => set({ date: e.target.value, time: '', tableId: null })} className="w-48" data-testid="resv-date" />
            {loadingAvail && !avail && <p className="text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Reading the book…</p>}
            {avail && !avail.is_open && <p className="text-sm text-amber-700 dark:text-amber-300">{avail.message || 'The restaurant is closed that day.'}</p>}
            {avail && avail.is_open && slots.length === 0 && <p className="text-sm text-slate-500">No start times left on this day.</p>}
            {avail && avail.is_open && slots.length > 0 && (
              <div className="grid grid-cols-4 sm:grid-cols-5 gap-1.5" data-testid="resv-slots">
                {slots.map((s) => {
                  const on = form.time === s.time;
                  const full = s.tables_available === 0;
                  return (
                    <button key={s.time} type="button" disabled={full && !on} onClick={() => set({ time: s.time })}
                      title={full ? 'No table free for this party' : `${s.tables_available} table${s.tables_available === 1 ? '' : 's'} free`}
                      className={`h-10 rounded-lg text-sm tabular-nums border transition
                        ${on ? 'border-teal-600 bg-teal-600 text-white shadow-sm' : full ? 'border-slate-100 dark:border-slate-800 text-slate-300 dark:text-slate-600 line-through cursor-not-allowed' : 'border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:border-teal-400'}`}>
                      {hhmm12(s.time).replace(' ', ' ')}
                    </button>
                  );
                })}
              </div>
            )}
            {avail?.sitting_minutes && form.time && <p className="text-xs text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Holds the table {avail.sitting_minutes} minutes.</p>}
          </Section>

          <Section title="Table" hint="Only tables that seat the party and are free for this sitting.">
            {!form.time ? <p className="text-sm text-slate-400">Pick a time first.</p> : (
              <div className="flex flex-wrap gap-1.5" data-testid="resv-tables">
                <button type="button" onClick={() => set({ tableId: null })}
                  className={`h-10 px-3 rounded-lg text-sm border ${form.tableId == null ? 'border-teal-600 bg-teal-50 text-teal-700 dark:bg-teal-500/10 dark:text-teal-300' : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'}`}>
                  Best free table
                </button>
                {freeTables.map((t) => (
                  <button key={t.id} type="button" onClick={() => set({ tableId: t.id })}
                    className={`h-10 px-3 rounded-lg text-sm border ${form.tableId === t.id ? 'border-teal-600 bg-teal-50 text-teal-700 dark:bg-teal-500/10 dark:text-teal-300' : 'border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:border-teal-400'}`}>
                    <span className="font-semibold">{t.table_number}</span>
                    <span className="text-xs text-slate-400 ml-1">{t.capacity}p{t.section ? ` · ${t.section}` : ''}</span>
                  </button>
                ))}
                {avail && freeTables.length === 0 && <p className="text-sm text-rose-600 w-full">No table is free for this party at {hhmm12(form.time)}.</p>}
              </div>
            )}
          </Section>

          <Section title="Details">
            <div className="flex flex-wrap gap-1.5">
              {OCCASIONS.map((o) => (
                <button key={o} type="button" onClick={() => set({ occasion: form.occasion === o ? '' : o })}
                  className={`h-8 px-3 rounded-full text-sm border ${form.occasion === o ? 'border-violet-500 bg-violet-50 text-violet-700 dark:bg-violet-500/10 dark:text-violet-300' : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'}`}>
                  {o}
                </button>
              ))}
            </div>
            <Input placeholder="Occasion (or type your own)" maxLength={50} value={form.occasion} onChange={(e) => set({ occasion: e.target.value })} />
            <div className="relative">
              <AlertTriangle className="w-4 h-4 text-rose-500 absolute left-2.5 top-2.5" />
              <Input className="pl-8" placeholder="Allergies (e.g. peanuts, shellfish)" maxLength={500} value={form.allergies} onChange={(e) => set({ allergies: e.target.value })} data-testid="resv-allergies" />
            </div>
            <Textarea rows={3} maxLength={1000} placeholder="Notes (high chair, window seat, running late…)" value={form.notes} onChange={(e) => set({ notes: e.target.value })} />
          </Section>

          <label className="flex items-start gap-3 rounded-xl border border-slate-200 dark:border-slate-700 p-3 cursor-pointer">
            <Checkbox checked={form.text} onCheckedChange={(v) => set({ text: v === true })} className="mt-0.5" data-testid="resv-text" />
            <span>
              <span className="text-sm font-medium text-slate-800 dark:text-slate-100 flex items-center gap-1.5"><MessageSquare className="w-4 h-4 text-teal-600" /> Text confirmation to guest</span>
              <span className="block text-xs text-slate-500 mt-0.5">Only if the guest agreed on the call. Sends the same text online bookings get, with the confirmation code{editing ? ' and the updated time' : ''}.</span>
            </span>
          </label>
        </div>

        <div className="border-t border-slate-100 dark:border-slate-800 px-6 py-4 space-y-2 bg-white dark:bg-slate-950">
          {problem && <p className="text-sm text-rose-600" role="alert" data-testid="resv-problem">{problem}</p>}
          {!slotFree && <p className="text-sm text-amber-700 dark:text-amber-300">That time has no free table for this party any more.</p>}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={saving}>Close</Button>
            <Button className="flex-1 bg-teal-600 hover:bg-teal-700 text-white" disabled={!ready || saving} onClick={save} data-testid="resv-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? 'Save changes' : `Book ${form.party} ${form.party === 1 ? 'guest' : 'guests'}${form.time ? ` at ${hhmm12(form.time)}` : ''}`}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
